import { ContractModel } from "../Contract.types.ts";
import { DotLegalDeleteDialog, DotLegalSelect } from "@dotlegal/dotlegal-ui-components";
import React from "react";
import { useResponsibleQuery } from "../../../user/hooks/useResponsibleQuery.ts";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { Trans } from "react-i18next";
import { useContractResponsibilityHelper } from "../ContractResponsibilityHelper.hooks.ts";

export interface ContractManagerFieldProps {
    /** Lets a caller that owns the vertical rhythm itself turn the field's own margin off. */
    noMargin?: boolean;
    isLoading: boolean;
    contract: ContractModel | undefined;
    onChange: (changeFunc: (contract: ContractModel) => void) => Promise<void>;
    readOnly: boolean;
    required: boolean;
    isDisabled: boolean;
    isPreview: boolean;
    errorText?: string;
    disableAccessWarning?: boolean;
    showWarningAsInfo?: boolean;
    /** Suppresses the field's help icon, for a view where the same field is shown twice. */
    hideToolTip?: boolean;
}

function ContractManagerField(props: ContractManagerFieldProps) {
    const { translateString } = useTranslation();
    const { onFieldChange, handleDialogConfirm, pendingUserChanges, setPendingUserChanges } = useContractResponsibilityHelper(
        props.onChange,
        props.disableAccessWarning
    );

    const userQuery = useResponsibleQuery(!props.isPreview);
    const isUserActive = userQuery.userData?.find((x) => x.id === props.contract?.contractManagerId)?.active ?? true;
    return (
        <>
            <DotLegalSelect
                noMargin={props.noMargin}
                options={
                    props.contract === undefined
                        ? []
                        : (userQuery.data(props.contract.contractManagerId) ?? []).filter((x) => !props.contract!.excludedUsers.includes(x.id))
                }
                isLoading={(userQuery.isLoading || props.isLoading) && !props.readOnly}
                onChange={
                    props.readOnly
                        ? () => {}
                        : (value) => {
                              let newContract = { ...props.contract!, contractManagerId: value! };
                              onFieldChange(newContract, props.contract!);
                          }
                }
                label={translateString("contractManager")}
                noOptionsLabel={translateString("noOptions")}
                toolTipText={
                    props.hideToolTip
                        ? undefined
                        : props.showWarningAsInfo
                          ? `${translateString("contractContractManagerTooltip")}\n\n${translateString("contractContractManagerWarning")}`
                          : translateString("contractContractManagerTooltip")
                }
                warningText={props.isPreview || props.showWarningAsInfo ? undefined : translateString("contractContractManagerWarning")}
                disableClearable={props.required}
                selectedItem={props.contract?.contractManagerId}
                disabled={props.isDisabled}
                errorText={props.errorText || (props.contract?.contractManagerId && !isUserActive) ? translateString("userDeactivated") : undefined}
            />

            {pendingUserChanges && (
                <DotLegalDeleteDialog
                    open
                    headerText={translateString("confirmAccessRemoval")}
                    alertBodyText={
                        <Trans
                            i18nKey="confirmContractAccessRemovalBody"
                            components={{
                                ul: <ul />,
                                li: <li />,
                            }}
                        />
                    }
                    warningTranslation={translateString("warning")}
                    deleteBtnText={translateString("save")}
                    cancelBtnText={translateString("cancel")}
                    onDialogClose={() => setPendingUserChanges(null)}
                    onOkClick={() => handleDialogConfirm()}
                />
            )}
        </>
    );
}

export default ContractManagerField;
